import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import StandardModal from "./StandardModal";
import {
  Calendar,
  Clock,
  User,
  Building2,
  Phone,
  Mail,
  CheckCircle,
  XCircle,
  AlertCircle,
  Crown,
  Search,
  Filter,
  Download,
  RefreshCw,
  Eye,
  Trash2,
  ChevronDown,
} from "lucide-react";

// Dropdown profesional (imitando Dashboard.js)
const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  icon: Icon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const selected = options.find((opt) => opt.value === value);
    setSelectedOption(selected);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "14px 16px",
          border: "2px solid #f1f5f9",
          borderRadius: "12px",
          background: disabled ? "#f8fafc" : "white",
          color: disabled ? "#94a3b8" : "#1e293b",
          fontSize: "16px",
          fontWeight: "500",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          outline: "none",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "44px",
          touchAction: "manipulation",
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "#dc2626";
          e.target.style.boxShadow = "0 0 0 3px rgba(220, 38, 38, 0.1)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "#f1f5f9";
          e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
          {Icon && <Icon size={16} color="#64748b" />}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            flexShrink: 0,
            color: "#64748b",
          }}
        />
      </button>
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
            marginTop: "4px",
            maxHeight: "200px",
            overflowY: "auto",
          }}
        >
          {options.map((option, index) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: option.value === value ? "#fee2e2" : "transparent",
                color: option.value === value ? "#dc2626" : "#374151",
                fontSize: "16px",
                fontWeight: option.value === value ? "600" : "400",
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
              onMouseEnter={(e) => {
                if (option.value !== value) {
                  e.target.style.background = "#f8fafc";
                }
              }}
              onMouseLeave={(e) => {
                if (option.value !== value) {
                  e.target.style.background = "transparent";
                }
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SystemDemos = ({ isMobile }) => {
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("todas");
  const [searchTerm, setSearchTerm] = useState("");
  const [updating, setUpdating] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, demoId: null });

  // Configuración anti-zoom
  useEffect(() => {
    if (typeof document !== "undefined") {
      let metaViewport = document.querySelector('meta[name="viewport"]');
      if (!metaViewport) {
        metaViewport = document.createElement("meta");
        metaViewport.name = "viewport";
        document.head.appendChild(metaViewport);
      }
      metaViewport.content =
        "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
    }
  }, []);

  // Scroll reset
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // ESC handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setSearchTerm("");
        setFilter("todas");
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    loadDemos();
  }, []);

  const loadDemos = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("demos_agendadas")
        .select("*")
        .order("fecha_creacion", { ascending: false });

      if (error) throw error;
      setDemos(data || []);
    } catch (error) {
      console.error("Error cargando demos:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateDemoStatus = async (demoId, newStatus) => {
    try {
      setUpdating(demoId);
      const { error } = await supabase
        .from("demos_agendadas")
        .update({ estado: newStatus })
        .eq("id", demoId);

      if (error) throw error;

      // Actualizar estado local
      setDemos(
        demos.map((demo) =>
          demo.id === demoId ? { ...demo, estado: newStatus } : demo
        )
      );
    } catch (error) {
      console.error("Error actualizando estado:", error);
    } finally {
      setUpdating(null);
    }
  };

  const deleteDemoRecord = async (demoId) => {
    setConfirmModal({ isOpen: true, demoId });
  };

  const handleConfirmDelete = async () => {
    const demoId = confirmModal.demoId;
    setConfirmModal({ isOpen: false, demoId: null });
    try {
      setUpdating(demoId);
      const { error } = await supabase
        .from("demos_agendadas")
        .delete()
        .eq("id", demoId);

      if (error) throw error;

      // Actualizar estado local
      setDemos(demos.filter((demo) => demo.id !== demoId));
    } catch (error) {
      console.error("Error eliminando demo:", error);
    } finally {
      setUpdating(null);
    }
  };

  const filteredDemos = demos.filter((demo) => {
    const matchesFilter = filter === "todas" || demo.estado === filter;
    const matchesSearch =
      !searchTerm ||
      demo.empresa.toLowerCase().includes(searchTerm.toLowerCase()) ||
      demo.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      demo.email.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  const getStatusColor = (estado) => {
    switch (estado) {
      case "pendiente":
        return { bg: "#fef3c7", color: "#92400e", border: "#fbbf24" };
      case "confirmada":
        return { bg: "#dbeafe", color: "#1e40af", border: "#3b82f6" };
      case "realizada":
        return { bg: "#d1fae5", color: "#065f46", border: "#10b981" };
      case "cancelada":
        return { bg: "#fee2e2", color: "#991b1b", border: "#ef4444" };
      default:
        return { bg: "#f3f4f6", color: "#374151", border: "#9ca3af" };
    }
  };

  const getStatusIcon = (estado) => {
    switch (estado) {
      case "pendiente":
        return <Clock size={14} />;
      case "confirmada":
        return <CheckCircle size={14} />;
      case "realizada":
        return <CheckCircle size={14} />;
      case "cancelada":
        return <XCircle size={14} />;
      default:
        return <AlertCircle size={14} />;
    }
  };

  const exportDemos = () => {
    const csv = [
      [
        "Fecha Creación",
        "Empresa",
        "Contacto",
        "Email",
        "Teléfono",
        "Horario Demo",
        "Tipo Obra",
        "Tamaño",
        "Estado",
      ],
      ...filteredDemos.map((demo) => [
        new Date(demo.fecha_creacion).toLocaleDateString("es-AR"),
        demo.empresa,
        demo.nombre,
        demo.email,
        demo.telefono,
        `${demo.fecha_demo} ${demo.hora_demo}`,
        demo.tipo_obra,
        demo.tamaño_empresa,
        demo.estado,
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `demos-nokk-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div
        style={{
          padding: isMobile ? "16px 12px 120px 12px" : "20px",
          maxWidth: "100%",
          margin: "0 auto",
          background: "#f8fafc",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              border: "4px solid #f3f4f6",
              borderTop: "4px solid #dc2626",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          ></div>
          <p style={{ color: "#64748b", fontSize: "16px" }}>
            Cargando demos...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <StandardModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, demoId: null })}
        title="¿Eliminar demo?"
        subtitle="Esta acción no se puede deshacer."
        icon={Trash2}
        isMobile={isMobile}
        gradient="linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)"
        maxWidth="400px"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <p style={{ color: "#991b1b", fontWeight: 500, fontSize: 15 }}>
            ¿Estás seguro de que deseas eliminar este registro de demo?
          </p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <button
              onClick={() => setConfirmModal({ isOpen: false, demoId: null })}
              style={{
                background: "#f3f4f6",
                color: "#374151",
                border: "none",
                borderRadius: "8px",
                padding: "10px 18px",
                fontWeight: 600,
                fontSize: "15px",
                cursor: "pointer",
              }}
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmDelete}
              style={{
                background: "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "10px 18px",
                fontWeight: 600,
                fontSize: "15px",
                cursor: "pointer",
              }}
            >
              Eliminar
            </button>
          </div>
        </div>
      </StandardModal>
      <div
        style={{
          padding: isMobile ? "16px 12px 120px 12px" : "20px",
          maxWidth: "100%",
          margin: "0 auto",
          background: "#f8fafc",
          minHeight: "100vh",
          animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            marginBottom: "24px",
            animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "16px",
              marginBottom: "20px",
              minHeight: "56px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flex: 1,
                minWidth: "200px",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 16px rgba(220, 38, 38, 0.25)",
                  flexShrink: 0,
                }}
              >
                <Crown size={20} color="white" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1
                  style={{
                    fontSize: isMobile ? "20px" : "24px",
                    fontWeight: "700",
                    color: "#0f172a",
                    margin: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Demos del Sistema
                </h1>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#64748b",
                    margin: 0,
                    fontWeight: "400",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Gestión de demos de todas las constructoras
                </p>
              </div>
            </div>

            {!isMobile && (
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  flexShrink: 0,
                  minWidth: "max-content",
                }}
              >
                <button
                  onClick={loadDemos}
                  style={{
                    background: "white",
                    color: "#64748b",
                    border: "2px solid #e2e8f0",
                    padding: "12px 20px",
                    borderRadius: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <RefreshCw size={16} />
                  Actualizar
                </button>
                <button
                  onClick={exportDemos}
                  style={{
                    background:
                      "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                    color: "white",
                    border: "none",
                    padding: "12px 20px",
                    borderRadius: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    boxShadow: "0 4px 16px rgba(220, 38, 38, 0.25)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    flexShrink: 0,
                    minWidth: "max-content",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Download size={16} />
                  Exportar CSV
                </button>
              </div>
            )}
          </div>

          {/* Filtros */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr 1fr",
              gap: "16px",
              marginBottom: "20px",
            }}
          >
            <div style={{ position: "relative" }}>
              <Search
                size={16}
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#64748b",
                  zIndex: 1,
                }}
              />
              <input
                type="text"
                placeholder="Buscar empresa, contacto o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus={false}
                autoComplete="off"
                style={{
                  width: "100%",
                  padding: "14px 16px 14px 44px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  WebkitAppearance: "none",
                  appearance: "none",
                  background: "white",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                  outline: "none",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxSizing: "border-box",
                  touchAction: "manipulation",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#dc2626";
                  e.target.style.boxShadow = "0 0 0 3px rgba(220, 38, 38, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                }}
              />
            </div>

            <CustomDropdown
              value={filter}
              onChange={setFilter}
              options={[
                { value: "todas", label: "Todas" },
                { value: "pendiente", label: "Pendientes" },
                { value: "confirmada", label: "Confirmadas" },
                { value: "realizada", label: "Realizadas" },
                { value: "cancelada", label: "Canceladas" },
              ]}
              placeholder="Estado"
              icon={Filter}
            />

            {isMobile && (
              <button
                onClick={exportDemos}
                style={{
                  background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                  color: "white",
                  border: "none",
                  padding: "14px 20px",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                }}
              >
                <Download size={16} />
                Exportar
              </button>
            )}
          </div>
        </div>

        {/* Contenido */}
        {filteredDemos.length === 0 ? (
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "60px 20px",
              textAlign: "center",
              border: "1px solid #f1f5f9",
              boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            }}
          >
            <Crown size={48} color="#dc2626" style={{ marginBottom: "16px" }} />
            <h3
              style={{
                color: "#64748b",
                fontSize: "18px",
                marginBottom: "8px",
                fontWeight: "600",
              }}
            >
              {filter === "todas"
                ? "No hay demos registradas"
                : `No hay demos ${filter}`}
            </h3>
            <p style={{ color: "#94a3b8", margin: 0 }}>
              {filter === "todas"
                ? "Las demos agendadas desde la landing aparecerán aquí"
                : `No se encontraron demos con estado "${filter}"`}
            </p>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gap: "16px",
            }}
          >
            {filteredDemos.map((demo, index) => {
              const statusStyle = getStatusColor(demo.estado);

              return (
                <div
                  key={demo.id}
                  style={{
                    background: "white",
                    border: "1px solid #f1f5f9",
                    borderRadius: "16px",
                    padding: "24px",
                    transition: "all 0.2s ease",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    animation: `slideInRight 0.${
                      5 + (index % 10)
                    }s cubic-bezier(0.4, 0, 0.2, 1)`,
                  }}
                >
                  <div
                    style={{
                      display: isMobile ? "block" : "grid",
                      gridTemplateColumns: "1fr 200px",
                      gap: "24px",
                      alignItems: "start",
                    }}
                  >
                    {/* Info principal */}
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          marginBottom: "16px",
                          flexWrap: "wrap",
                        }}
                      >
                        <h3
                          style={{
                            fontSize: "20px",
                            fontWeight: "700",
                            color: "#1e293b",
                            margin: 0,
                          }}
                        >
                          {demo.empresa}
                        </h3>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            padding: "4px 12px",
                            borderRadius: "20px",
                            fontSize: "12px",
                            fontWeight: "600",
                            background: statusStyle.bg,
                            color: statusStyle.color,
                            border: `1px solid ${statusStyle.border}`,
                          }}
                        >
                          {getStatusIcon(demo.estado)}
                          {demo.estado}
                        </span>
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isMobile
                            ? "1fr"
                            : "repeat(auto-fit, minmax(250px, 1fr))",
                          gap: "16px",
                          marginBottom: "16px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            color: "#64748b",
                          }}
                        >
                          <User size={16} />
                          <span>{demo.nombre}</span>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            color: "#64748b",
                          }}
                        >
                          <Mail size={16} />
                          <a
                            href={`mailto:${demo.email}`}
                            style={{
                              color: "#dc2626",
                              textDecoration: "none",
                            }}
                          >
                            {demo.email}
                          </a>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            color: "#64748b",
                          }}
                        >
                          <Phone size={16} />
                          <a
                            href={`tel:${demo.telefono}`}
                            style={{
                              color: "#dc2626",
                              textDecoration: "none",
                            }}
                          >
                            {demo.telefono}
                          </a>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            color: "#64748b",
                          }}
                        >
                          <Calendar size={16} />
                          <span>
                            {demo.fecha_demo} - {demo.hora_demo}
                          </span>
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: "12px",
                          marginBottom: "12px",
                          fontSize: "14px",
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            background: "#eff6ff",
                            color: "#2563eb",
                            padding: "4px 12px",
                            borderRadius: "20px",
                            fontWeight: "600",
                          }}
                        >
                          {demo.tipo_obra}
                        </span>
                        <span
                          style={{
                            background: "#f0fdf4",
                            color: "#16a34a",
                            padding: "4px 12px",
                            borderRadius: "20px",
                            fontWeight: "600",
                          }}
                        >
                          {demo.tamaño_empresa}
                        </span>
                      </div>

                      {demo.desafio_principal && (
                        <div
                          style={{
                            background: "#f8fafc",
                            padding: "12px 16px",
                            borderRadius: "8px",
                            marginTop: "12px",
                          }}
                        >
                          <p
                            style={{
                              fontSize: "14px",
                              color: "#64748b",
                              margin: 0,
                              fontStyle: "italic",
                            }}
                          >
                            "{demo.desafio_principal}"
                          </p>
                        </div>
                      )}

                      <div
                        style={{
                          fontSize: "11px",
                          color: "#94a3b8",
                          marginTop: "12px",
                        }}
                      >
                        Registrado:{" "}
                        {new Date(demo.fecha_creacion).toLocaleDateString(
                          "es-AR",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          }
                        )}
                      </div>
                    </div>

                    {/* Acciones */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: isMobile ? "row" : "column",
                        gap: "8px",
                        marginTop: isMobile ? "16px" : "0",
                      }}
                    >
                      {demo.estado === "pendiente" && (
                        <button
                          onClick={() => updateDemoStatus(demo.id, "confirmada")}
                          disabled={updating === demo.id}
                          style={{
                            background: "#10b981",
                            color: "white",
                            border: "none",
                            padding: "8px 16px",
                            borderRadius: "6px",
                            fontSize: "13px",
                            fontWeight: "600",
                            cursor:
                              updating === demo.id ? "not-allowed" : "pointer",
                            opacity: updating === demo.id ? 0.6 : 1,
                            flex: isMobile ? 1 : "none",
                          }}
                        >
                          {updating === demo.id ? "Actualizando..." : "Confirmar"}
                        </button>
                      )}

                      {demo.estado === "confirmada" && (
                        <button
                          onClick={() => updateDemoStatus(demo.id, "realizada")}
                          disabled={updating === demo.id}
                          style={{
                            background: "#3b82f6",
                            color: "white",
                            border: "none",
                            padding: "8px 16px",
                            borderRadius: "6px",
                            fontSize: "13px",
                            fontWeight: "600",
                            cursor:
                              updating === demo.id ? "not-allowed" : "pointer",
                            opacity: updating === demo.id ? 0.6 : 1,
                            flex: isMobile ? 1 : "none",
                          }}
                        >
                          {updating === demo.id
                            ? "Actualizando..."
                            : "Marcar Realizada"}
                        </button>
                      )}

                      {(demo.estado === "pendiente" ||
                        demo.estado === "confirmada") && (
                        <button
                          onClick={() => updateDemoStatus(demo.id, "cancelada")}
                          disabled={updating === demo.id}
                          style={{
                            background: "#ef4444",
                            color: "white",
                            border: "none",
                            padding: "8px 16px",
                            borderRadius: "6px",
                            fontSize: "13px",
                            fontWeight: "600",
                            cursor:
                              updating === demo.id ? "not-allowed" : "pointer",
                            opacity: updating === demo.id ? 0.6 : 1,
                            flex: isMobile ? 1 : "none",
                          }}
                        >
                          {updating === demo.id ? "Cancelando..." : "Cancelar"}
                        </button>
                      )}

                      <button
                        onClick={() => deleteDemoRecord(demo.id)}
                        disabled={updating === demo.id}
                        style={{
                          background: "#f8fafc",
                          color: "#64748b",
                          border: "1px solid #e2e8f0",
                          padding: "8px 16px",
                          borderRadius: "6px",
                          fontSize: "13px",
                          fontWeight: "600",
                          cursor:
                            updating === demo.id ? "not-allowed" : "pointer",
                          opacity: updating === demo.id ? 0.6 : 1,
                          flex: isMobile ? 1 : "none",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "4px",
                        }}
                      >
                        <Trash2 size={12} />
                        {updating === demo.id ? "Eliminando..." : "Eliminar"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <style jsx>{`
          @keyframes spin {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }

          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes slideInRight {
            from {
              opacity: 0;
              transform: translateX(20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

          @media (max-width: 768px) {
            input,
            textarea,
            select,
            button {
              font-size: 16px !important;
              -webkit-appearance: none !important;
              appearance: none !important;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default SystemDemos;
